import { useEffect, useState } from 'react';
import { cn } from '@/lib/utils';
import { EliteAngularLoader, EliteScanBar } from './EliteLoading';
import { MatGridOverlay } from './MatGridOverlay';
import { Check } from 'lucide-react';

export interface TimelineStep {
    id: string;
    label: string;
    durationMs: number; // How long this step takes to complete
}

export function AnalysisTimeline({
    isVisible,
    onComplete,
    title = "Systems Engine",
    steps = [
        { id: '1', label: 'INITIALIZING GRID', durationMs: 1500 },
        { id: '2', label: 'ANALYZING MATCH PATTERNS', durationMs: 2000 },
        { id: '3', label: 'SYNTHESIZING KINETIC DATA', durationMs: 2000 },
        { id: '4', label: 'COMPILING INTELLIGENCE', durationMs: 1500 },
    ]
}: {
    isVisible: boolean;
    onComplete?: () => void;
    title?: string;
    steps?: TimelineStep[];
}) {
    const [currentStepIndex, setCurrentStepIndex] = useState(0);

    useEffect(() => {
        if (!isVisible) {
            setCurrentStepIndex(0);
            return;
        }

        let isCancelled = false;

        const runTimeline = async () => {
            for (let i = 0; i < steps.length; i++) {
                setCurrentStepIndex(i);
                await new Promise(r => setTimeout(r, steps[i]?.durationMs || 1000));
                if (isCancelled) return;
            }
            if (onComplete) onComplete();
        };

        runTimeline();

        return () => { isCancelled = true; };
    }, [isVisible, steps, onComplete]);

    if (!isVisible) return null;

    return (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-bg/95 backdrop-blur-md animate-fade-in overflow-hidden" aria-live="polite">
            <MatGridOverlay isActive={true} />

            <div className="relative z-10 w-full max-w-sm mx-4 flex flex-col items-center">
                <div className="mb-8 flex flex-col items-center animate-fade-in-up">
                    <EliteAngularLoader className="w-16 h-16 mb-4" />
                    <h2 className="text-gold-500 font-black uppercase tracking-[0.2em] text-sm drop-shadow-[0_0_10px_rgba(212,175,55,0.5)]">
                        {title}
                    </h2>
                </div>

                <div className="bg-bg-surface/80 border border-white/10 shadow-card-elite rounded-xl p-6 relative overflow-hidden backdrop-blur-2xl w-full animate-fade-in-up" style={{ animationDelay: '200ms' }}>
                    <EliteScanBar className="absolute top-0 left-0 right-0" />

                    <div className="space-y-5 relative z-10 py-2">
                        {steps.map((step, index) => {
                            const isPast = index < currentStepIndex;
                            const isCurrent = index === currentStepIndex;

                            return (
                                <div key={step.id} className={cn(
                                    "flex items-center gap-4 transition-all duration-500",
                                    isPast ? "opacity-70" : isCurrent ? "opacity-100 scale-[1.02] translate-x-1" : "opacity-30"
                                )}>
                                    <div className={cn(
                                        "w-5 h-5 rounded-sm border flex items-center justify-center transition-all duration-500 flex-shrink-0 relative overflow-hidden",
                                        isPast ? "bg-gold-500/10 border-gold-500/50 text-gold-400" :
                                            isCurrent ? "border-gold-500 bg-gold-500/5 shadow-[0_0_10px_rgba(212,175,55,0.4)]" :
                                                "border-white/10 bg-transparent text-transparent"
                                    )}>
                                        {isPast && <Check size={12} strokeWidth={3} className="animate-scale-in" />}
                                        {isCurrent && <div className="w-2 h-2 bg-gold-400 rounded-[1px] animate-pulse" />}
                                    </div>
                                    <span className={cn(
                                        "text-xs font-black uppercase tracking-[0.15em] leading-none pt-0.5 transition-colors duration-500",
                                        isPast ? "text-gold-400/80" :
                                            isCurrent ? "text-white" :
                                                "text-text-muted"
                                    )}>
                                        {step.label}
                                    </span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </div>
        </div>
    );
}
